import React, { useState, useEffect, useMemo } from 'react';
import { Button, FormGroup, Label, Input, Row, Col } from 'reactstrap';
import { H5 } from '../../../../AbstractElements';
import { fetchFormData, createExpertReport } from '../../../../Api/ApiMethods';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';
import { getFieldAccess } from '../../../../_helper/fieldAccess';

const ExpertReportForm = ({ formData, fieldPermissions, canEdit = true, callFormState }) => {
    const { id } = useParams();
    const [reportTemplates, setReportTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [selectedFileNumber, setSelectedFileNumber] = useState('');
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [creatingReport, setCreatingReport] = useState(false);

    const reapplicationStates = ['Yeni Eksper İşlem Bekleniyor', 'Yeni Eksper Rapor Bekleniyor', 'Yeni Eksper Raporu Ödeme Bekleniyor', 'Yeni Sigorta Başvuru Dilekçe Bekleniyor', 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor'];
    const insuranceOnlyState = 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor';
    const isInsuranceOnlyWorkflow = callFormState === insuranceOnlyState;
    const isReapplicationWorkflow = reapplicationStates.includes(callFormState);
    const requiresReapplicationFlag = isReapplicationWorkflow && !isInsuranceOnlyWorkflow;

    const fileTemplateApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + "/filetemplate";
    const createReportApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + "/Report/create-report";
    useEffect(() => {
        const fetchReportTemplates = async () => {
            setLoadingTemplates(true);
            try {
                const response = await fetchFormData({
                    apiEndpoint: fileTemplateApiUrl,
                    page: 1,
                    pageSize: 100,
                    search: null,
                });

                console.log('Report templates API response:', response);

                let templates = [];
                if (response && response.isSuccess && response.data && Array.isArray(response.data.items)) {
                    templates = response.data.items.filter(template => template.isExpertReport === true);
                } else if (response && Array.isArray(response.items)) {
                    templates = response.items.filter(template => template.isExpertReport === true);
                } else if (Array.isArray(response)) {
                    templates = response.filter(template => template.isExpertReport === true);
                }

                setReportTemplates(templates);
                console.log('Eksper rapor şablonları yüklendi:', templates);
            } catch (error) {
                console.error('Error fetching report templates:', error);
                toast.error('Rapor şablonları yüklenirken bir hata oluştu.');
                setReportTemplates([]);
            } finally {
                setLoadingTemplates(false);
            }
        };

        fetchReportTemplates();
    }, [fileTemplateApiUrl]);

    const isLorFileType = (title) => {
        if (!title) return false;
        const lowerTitle = title.toLowerCase();
        return lowerTitle.includes('hak mahrumiyeti') ||
               lowerTitle.includes('lor') ||
               lowerTitle.includes('mahrumiyeti');
    };

    const availableFileOptions = useMemo(() => {
        if (!formData.filesToBeWorked || formData.filesToBeWorked.length === 0) {
            return [];
        }

        return formData.filesToBeWorked.map((accidentFileEntry, index) => {
            const matchedAccidentFile = accidentFileEntry.accidentFileId
                ? formData.accidentFiles?.find(af => af.id === accidentFileEntry.accidentFileId)
                : null;

            const fileNumber = accidentFileEntry.fileNumber || (
                matchedAccidentFile ? matchedAccidentFile.fileNumber : (accidentFileEntry.accidentFileId || index.toString())
            );

            let allFileTypeTitles = [];

            if (accidentFileEntry.fileType && !accidentFileEntry.filesToBeWorked) {
                allFileTypeTitles = accidentFileEntry._fileTypeTitles || [];
            } else {
                accidentFileEntry.filesToBeWorked?.forEach(file => {
                    if (file._fileTypeTitles && Array.isArray(file._fileTypeTitles)) {
                        allFileTypeTitles.push(...file._fileTypeTitles);
                    }
                });
            }

            const hasNonLorTypes = allFileTypeTitles.some(title => !isLorFileType(title));

            if (!hasNonLorTypes && allFileTypeTitles.length > 0) {
                return null;
            }

            const isReapplicationFile = accidentFileEntry.isReapplicationFile === true || matchedAccidentFile?.isReapplicationFile === true;
            const isFromInsurance = accidentFileEntry.isFromInsurance === true || matchedAccidentFile?.isFromInsurance === true;

            if (isInsuranceOnlyWorkflow && !isFromInsurance) {
                return null;
            }

            if (requiresReapplicationFlag && !isReapplicationFile) {
                return null;
            }

            return {
                value: accidentFileEntry.accidentFileId || index.toString(),
                label: fileNumber,
                accidentFileId: accidentFileEntry.accidentFileId,
                hasNonLorTypes,
                fileTypeTitles: allFileTypeTitles,
                isReapplicationFile: isReapplicationFile || false,
                isFromInsurance: isFromInsurance || false,
            };
        }).filter(item => item !== null);
    }, [formData.filesToBeWorked, formData.accidentFiles, isInsuranceOnlyWorkflow, requiresReapplicationFlag]);

    useEffect(() => {
        if (selectedFileNumber && !availableFileOptions.some(option => option.value === selectedFileNumber)) {
            setSelectedFileNumber('');
        }
    }, [availableFileOptions, selectedFileNumber]);

    const generateReportAccess = getFieldAccess(fieldPermissions, 'expertreport.generate', canEdit);
    const handleCreateReport = async () => {
        if (!selectedFileNumber) {
            toast.error('Lütfen bir dosya numarası seçin.');
            return;
        }
        
        if (!selectedTemplate) {
            toast.error('Lütfen bir rapor şablonu seçin.');
            return;
        }

        if (!id) {
            toast.error('Çağrı formu ID bulunamadı.');
            return;
        }

        const selectedOption = availableFileOptions.find(option => option.value === selectedFileNumber);
        if (!selectedOption) {
            toast.error('Seçilen dosya mevcut değil veya düzenlemeye kapalı.');
            setSelectedFileNumber('');
            return;
        }

        setCreatingReport(true);
        try {
            await createExpertReport({
                apiEndpoint: createReportApiUrl,
                callFormId: id,
                reportTemplateId: selectedTemplate,
                accidentFileId: selectedFileNumber
            });
            
            setSelectedTemplate('');
            setSelectedFileNumber('');
        } catch (error) {
            console.error('Error creating report:', error);
        } finally {
            setCreatingReport(false);
        }
    };return (
        <>
            <H5>Eksper Raporları</H5>
            <div style={{ height: "20px" }}></div>

            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label for="fileNumberSelect">Dosya No <span className="text-danger">*</span></Label>
                        <Input
                            id="fileNumberSelect"
                            type="select"
                            value={selectedFileNumber}
                            onChange={(e) => setSelectedFileNumber(e.target.value)}
                            disabled={!canEdit || generateReportAccess.disabled || availableFileOptions.length === 0}
                        >
                            <option value="">Dosya numarası seçiniz...</option>
                            {availableFileOptions.map((fileOption) => (
                                <option key={fileOption.value} value={fileOption.value}>
                                    {fileOption.label} ({fileOption.fileTypeTitles.filter(title => !isLorFileType(title)).join(', ')})
                                </option>
                            ))}
                        </Input>
                        {availableFileOptions.length === 0 && (
                            <small className="text-muted">Henüz eksper raporu oluşturulabilecek dosya bulunmamaktadır. (Sadece Hak Mahrumiyeti içeren dosyalar için rapor oluşturulamaz.)</small>
                        )}
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label for="templateSelect">Rapor Türü <span className="text-danger">*</span></Label>
                        <Input
                            id="templateSelect"
                            type="select"
                            value={selectedTemplate}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                            disabled={loadingTemplates || !canEdit || generateReportAccess.disabled}
                        >
                            <option value="">Rapor türü seçiniz...</option>
                            {reportTemplates.map((template) => (
                                <option key={template.id} value={template.id}>
                                    {template.title}
                                </option>
                            ))}
                        </Input>
                        {loadingTemplates && (
                            <small className="text-muted">Rapor şablonları yükleniyor...</small>
                        )}
                        {!loadingTemplates && reportTemplates.length === 0 && (
                            <small className="text-muted">Henüz rapor şablonu bulunmamaktadır.</small>
                        )}
                    </FormGroup>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                    <FormGroup className="mb-0">
                        <Button 
                            color="primary" 
                            onClick={handleCreateReport}
                            disabled={!selectedFileNumber || !selectedTemplate || creatingReport || loadingTemplates || !canEdit || generateReportAccess.disabled}
                        >
                            {creatingReport ? 'Oluşturuluyor...' : 'Rapor Oluştur'}
                        </Button>
                    </FormGroup>
                </Col>
            </Row>
        </>
    );
};

export default ExpertReportForm;
